// Story 8.15 (ADO-2024), D-1, D-2, D-3, D-5: the Auto-send control. The Shopper ticks it to have
// the cart sent automatically, with what it holds then, a set number of minutes before its
// cut-off. The opt-in is the cart's, shared by everyone on it, and held on the server (D-3): the
// box shows the basket store's `autoSend` (from the cart read), or the state just asked for while
// that write is in flight.
//
//   <AutoSendToggle variant="page" cutOffStatus={cutOff.status} />                // Order Review
//   <AutoSendToggle variant="panel" cutOffStatus={cutOff.status} onNavigate={close} />
//
// - Where. `page`: beside the depot name in the Order Review's depot card header (Figma
//   `5008:3087` in `5006:2501`), "Auto-send" alone. `panel`: in the side panel's second header
//   row, after the lines pill (Figma "Auto-send toggle" `5988:78274`), "Auto-send" (semibold) then
//   "N min before cut-off" (regular).
// - Hidden, not disabled (D-2, D-6), unless the cart has a cut-off to send before
//   (`autoSendOffered` over the host's `useDepotCutOff` status: one shared read per cart, 8.5
//   D-15), the cart read has named the lead time (the copy is derived from that wire value, D-1),
//   and a cart is open.
// - What is armed (D-5). On the review, ticking sends the review's reference and fulfilment
//   choice (`autoSendChoicesFor`, what a send from the review would carry), and while it is ticked
//   a change the Shopper makes to either is re-saved after a short pause (and at once when the
//   review is left). The review always sends its reference, empty when the field is blank, so the
//   server stores what the review shows. On an armed cart the review starts from the armed choices
//   (`useOrderSubmissionStore` seeds them from the cart read where the Shopper holds nothing), so
//   a re-save after a reload carries them on with the edit. In the panel, ticking sends the cart's
//   reference alone: no fulfilment, so the order goes as a plain delivery with no requested date;
//   the reference the Shopper holds for the cart (empty once cleared), or none at all when they
//   hold no entry, which leaves the server the reference the cart kept. Ticked, the panel's box
//   only unticks: it never re-saves an armed cart's choices. An account that requires a reference
//   with none held is pointed to Order Review instead of offered the box, unless the cart is
//   already armed (so it can be unticked).
// - Accessibility. The accessible name is "Auto-send N min before cut-off" in both places (the
//   review keeps the lead time visually hidden; the visible "Auto-send" starts the name, WCAG
//   2.5.3). The armed state is the native checkbox's own, which a screen reader announces when the
//   Shopper toggles it, inside the side panel's `aria-modal` dialog as anywhere else, so it needs
//   no live region of its own. A change that did not land is an inline `role="alert"` beside the
//   box, inside the dialog, so it is heard there too.

import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import type { CutOffStatus } from '../api/basket.client';
import { cartTargetKey } from '../api/cart-target';
import { Checkbox } from '../design-system';
import { autoSendLeadCopy, autoSendOffered } from './autoSend';
import { useSessionCartTarget } from './sessionCart';
import { useBasketStore } from './useBasketStore';
import { useOrderReferenceRule } from './useOrderReferenceRule';
import { autoSendChoicesFor, useOrderSubmissionStore } from './useOrderSubmissionStore';
import styles from './AutoSendToggle.module.css';

/** How long the review waits after the Shopper's last change before re-saving an armed cart's
 *  choices. */
export const AUTO_SEND_RESAVE_DELAY_MS = 600;

/** The Order Review route, where the side panel points a reference-required account. */
const ORDER_REVIEW_ROUTE = '/cart';

export interface AutoSendToggleProps {
  readonly variant: 'page' | 'panel';
  /** The cart's cut-off as the host's `useDepotCutOff` reads it; `null` while unknown. */
  readonly cutOffStatus: CutOffStatus | null;
  /** The panel's link to Order Review was followed (the host closes the panel). */
  readonly onNavigate?: () => void;
}

/** Whether the control shows for the session cart under `cutOffStatus`: the host's row can
 *  then tell whether it has anything to show. */
export function useAutoSendShown(cutOffStatus: CutOffStatus | null): boolean {
  const leadKnown = useBasketStore((s) => s.autoSendLeadMinutes !== null);
  const cartOpen = useBasketStore((s) => s.cartId !== null);
  return leadKnown && cartOpen && autoSendOffered(cutOffStatus);
}

/**
 * While the cart is armed, re-save the review's choices after the Shopper changes its reference
 * or its fulfilment choice (D-5): after {@link AUTO_SEND_RESAVE_DELAY_MS} without a further
 * change, or at once on unmount. Only the Shopper's own edits count (`shopperEditsByCart`): the
 * review seeded from the arming or from what the cart kept, and the Fulfilment card registering
 * its defaults, save nothing, so opening or reloading the review saves nothing. A re-save is
 * dropped once the basket store was reset since the change (a change of cart).
 */
function useReviewResave(cartKey: string | null): void {
  const edits = useOrderSubmissionStore((s) =>
    cartKey === null ? undefined : s.shopperEditsByCart[cartKey],
  );
  const baseline = useRef<{
    readonly cartKey: string | null;
    readonly edits: number | undefined;
  } | null>(null);
  const waiting = useRef<{
    readonly timer: ReturnType<typeof setTimeout>;
    readonly fire: () => void;
  } | null>(null);

  useEffect(() => {
    const seen = baseline.current;
    baseline.current = { cartKey, edits };
    if (seen === null || seen.cartKey !== cartKey || cartKey === null) return;
    if (seen.edits === edits) return;
    if (waiting.current !== null) clearTimeout(waiting.current.timer);
    const generation = useBasketStore.getState().generation();
    const fire = () => {
      waiting.current = null;
      const basket = useBasketStore.getState();
      if (basket.generation() !== generation) return;
      const armed = basket.autoSendPending ?? basket.autoSend !== null;
      if (!armed) return;
      void basket.setAutoSend(true, autoSendChoicesFor(cartKey));
    };
    waiting.current = { timer: setTimeout(fire, AUTO_SEND_RESAVE_DELAY_MS), fire };
  }, [cartKey, edits]);

  useEffect(
    () => () => {
      const pending = waiting.current;
      if (pending === null) return;
      clearTimeout(pending.timer);
      pending.fire();
    },
    [],
  );
}

export function AutoSendToggle({ variant, cutOffStatus, onNavigate }: AutoSendToggleProps) {
  const cart = useSessionCartTarget();
  const cartKey = cart === null ? null : cartTargetKey(cart);
  const shown = useAutoSendShown(cutOffStatus);
  const leadMinutes = useBasketStore((s) => s.autoSendLeadMinutes);
  const armed = useBasketStore((s) => s.autoSend !== null);
  const pending = useBasketStore((s) => s.autoSendPending);
  const error = useBasketStore((s) => s.autoSendError);
  const heldEntry = useOrderSubmissionStore((s) =>
    cartKey === null ? undefined : s.customerOrderReferenceByCart[cartKey],
  );
  const heldReference = heldEntry?.trim() ?? '';
  // The panel alone reads the rule; the review's reference field already does.
  const rule = useOrderReferenceRule(variant === 'panel' && shown ? cart : null);
  useReviewResave(variant === 'page' ? cartKey : null);

  if (!shown || leadMinutes === null || cartKey === null) return null;

  const leadCopy = autoSendLeadCopy(leadMinutes);
  const checked = pending ?? armed;
  const panel = variant === 'panel';

  if (panel && !checked && rule?.required === true && heldReference === '') {
    return (
      <Link
        to={ORDER_REVIEW_ROUTE}
        className={styles.reviewLink}
        data-variant={variant}
        onClick={onNavigate}
      >
        Auto-send needs an order reference: add it on Order Review
      </Link>
    );
  }

  const toggle = () => {
    const setAutoSend = useBasketStore.getState().setAutoSend;
    if (checked) {
      void setAutoSend(false, null);
      return;
    }
    void setAutoSend(
      true,
      panel
        ? heldEntry === undefined
          ? {}
          : { customerOrderReference: heldReference }
        : autoSendChoicesFor(cartKey),
    );
  };

  return (
    <span className={styles.root} data-variant={variant}>
      <Checkbox
        checked={checked}
        onChange={toggle}
        aria-busy={pending !== null ? true : undefined}
        label={
          <span className={styles.label}>
            <span className={styles.name}>Auto-send</span>{' '}
            <span
              className={panel ? styles.lead : styles.visuallyHidden}
              data-part={panel ? 'lead' : 'lead-hidden'}
            >
              {leadCopy}
            </span>
          </span>
        }
      />
      {error === null ? null : (
        <span className={styles.error} role="alert">
          {error.message}
          {panel && error.kind === 'reference' ? (
            <>
              {' '}
              <Link to={ORDER_REVIEW_ROUTE} className={styles.errorLink} onClick={onNavigate}>
                Add it on Order Review
              </Link>
            </>
          ) : null}
        </span>
      )}
    </span>
  );
}
